import { keepPreviousData, UseQueryOptions } from '@tanstack/react-query';
import { sumTransactions } from '@unistocks/features/portfolios/portfolio-utils';
import { BucketDto, ListResultDtoOfBucketDto } from '@unistocks/proxy/axios-client';
import {
  usePortfoliosGetTimeSeriesQuery
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import {
  useSharedPortfoliosGetTimeSeriesQuery
} from '@unistocks/proxy/axios-client/SharedPortfoliosQuery';
import { createDate, today } from '@unistocks/shared/utils/util';
import { DateTime } from 'luxon';

const createSelectFunction = (
  start: DateTime,
  endDate: DateTime,
  startMillis: number,
  endMillis: number
) => {
  return ({ items: buckets }: ListResultDtoOfBucketDto) => {
    const res: BucketDto = {
      portfolioId: buckets[0].portfolioId,
      startDate: start.toISODate(),
      endDate: endDate.toISODate(),
      currency: buckets[0].currency,
      initialValue: buckets[0].initialValue,
      entries: [],
      transactions: [],
      netDeposits: 0
    };

    const entries = buckets.flatMap(x => x.entries);
    const startIndex = entries.findIndex(x => x.timestamp >= startMillis);
    const endIndex = entries.findIndex(x => x.timestamp >= endMillis);

    res.entries = entries.slice(
      startIndex === -1 ? 0 : startIndex,
      endIndex === -1 ? undefined : endIndex + 1
    );

    res.transactions = buckets
      .flatMap(x => x.transactions)
      .filter(x => {
        const date = createDate(x.date);

        return date >= start && date <= endDate;
      });
    res.netDeposits = sumTransactions(res.transactions);

    return res;
  };
};

export const usePortfolioHistory = (
  portfolioId: string,
  start: DateTime,
  end?: DateTime,
  options?: Omit<UseQueryOptions<ListResultDtoOfBucketDto, unknown, BucketDto>, 'queryKey'>
) => {
  const endDate = end ?? today();
  const queryStartISO = start.startOf('year').toISODate();
  const queryEndISO = endDate.endOf('year').toISODate();
  const startMillis = start.toMillis();
  const endMillis = endDate.toMillis();

  const select = createSelectFunction(start, endDate, startMillis, endMillis);

  return usePortfoliosGetTimeSeriesQuery(
    {
      id: portfolioId,
      start: queryStartISO,
      end: queryEndISO,
      includeTransactions: true
    },
    {
      select,
      placeholderData: keepPreviousData,
      ...options
    }
  );
};

export const useSharedPortfolioHistory = (
  portfolioId: string,
  start: DateTime,
  end?: DateTime,
  options?: Omit<UseQueryOptions<ListResultDtoOfBucketDto, unknown, BucketDto>, 'queryKey'>
) => {
  const endDate = end ?? today();
  const queryStartISO = start.startOf('year').toISODate();
  const queryEndISO = endDate.endOf('year').toISODate();
  const startMillis = start.toMillis();
  const endMillis = endDate.toMillis();

  const select = createSelectFunction(start, endDate, startMillis, endMillis);

  return useSharedPortfoliosGetTimeSeriesQuery(
    {
      id: portfolioId,
      start: queryStartISO,
      end: queryEndISO,
      includeTransactions: true
    },
    {
      select,
      placeholderData: keepPreviousData,
      ...options
    }
  );
};
